import { inject, Injectable } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';

import {
  CAPTURING_GROUP_PATTERN,
  ESCAPE_REPLACEMENT,
  ESCAPED_VARIABLE_PATTERN,
  MESSAGE_TO_KEY,
  REGEX_SPECIAL_CHARS,
  TEMPLATE_VARIABLE_PATTERN,
} from './translate-backend.constants';

@Injectable({ providedIn: 'root' })
export class TranslateBackendService {
  // Injections
  readonly #translocoService = inject(TranslocoService);

  // Variables
  readonly #MESSAGE_TO_KEY = MESSAGE_TO_KEY;

  /**
   * Переводит сообщение об ошибке с бэкенда на текущий язык пользователя
   *
   * Алгоритм работы:
   * 1. Проверяет точное совпадение сообщения с шаблоном без переменных
   * 2. Если не найдено - перебирает шаблоны с переменными
   * 3. Для каждого шаблона извлекает переменные и пытается сопоставить с сообщением
   * 4. При успешном сопоставлении - возвращает переведенное сообщение с подставленными переменными
   * 5. Если ничего не найдено - возвращает оригинальное сообщение
   *
   * @param message - сообщение об ошибке с бэкенда
   * @returns переведенное сообщение или оригинальное, если перевод не найден
   *
   * @example
   * backendMessageToKey('Forbidden: Invalid password')
   * // => 'Неверный пароль'
   *
   * @example
   * backendMessageToKey('Bad Request: Slug "my-slug" is already taken by another user')
   * // => 'Слаг "my-slug" уже занят другим пользователем'
   */
  backendMessageToKey(message: string): string {
    // Шаг 1: Пробуем найти точное совпадение (для сообщений без переменных)
    const exactKey = this.#MESSAGE_TO_KEY[message];

    if (exactKey) {
      return this.#translocoService.translate(exactKey);
    }

    // Шаг 2: Перебираем все шаблоны для поиска совпадения с переменными
    for (const [template, translationKey] of Object.entries(this.#MESSAGE_TO_KEY)) {
      const result = this.#matchTemplate(template, message);

      if (result) {
        // Шаг 3: Найдено совпадение - переводим с параметрами
        return this.#translocoService.translate(translationKey, result);
      }
    }

    // Шаг 4: Ничего не найдено - возвращаем оригинальное сообщение
    return message;
  }

  /**
   * Проверяет, доступен ли перевод для сообщения об ошибке с бэкенда
   *
   * Алгоритм работы:
   * 1. Проверяет точное совпадение сообщения с шаблоном без переменных
   * 2. Если не найдено - перебирает шаблоны с переменными
   * 3. Для каждого шаблона пытается сопоставить с сообщением
   * 4. Возвращает true при первом успешном совпадении
   *
   * @param message - сообщение об ошибке с бэкенда
   * @returns true, если перевод доступен; false, если нет
   *
   * @example
   * hasTranslation('Forbidden: Invalid password')
   * // => true
   *
   * @example
   * hasTranslation('Bad Request: Slug "my-slug" is already taken by another user')
   * // => true
   *
   * @example
   * hasTranslation('Unknown error message')
   * // => false
   */
  hasTranslation(message: string): boolean {
    // Шаг 1: Проверяем точное совпадение (для сообщений без переменных)
    if (this.#MESSAGE_TO_KEY[message]) {
      return true;
    }

    // Шаг 2: Перебираем все шаблоны для поиска совпадения с переменными
    for (const template of Object.keys(this.#MESSAGE_TO_KEY)) {
      const result = this.#matchTemplate(template, message);

      if (result) {
        return true;
      }
    }

    // Шаг 3: Ничего не найдено
    return false;
  }

  /**
   * Сопоставляет шаблон с сообщением и извлекает значения переменных
   *
   * @param template - шаблон с переменными, например: 'User {name} tried {action}'
   * @param message - реальное сообщение, например: 'User "John" tried "login"'
   * @returns объект с переменными и их значениями или null, если не совпало
   *
   * @example
   * matchTemplate('User {name} tried {action}', 'User "John" tried "login"')
   * // => { name: '"John"', action: '"login"' }
   */
  #matchTemplate(template: string, message: string): Record<string, string> | null {
    // Шаг 1: Извлекаем имена переменных из шаблона (например: ['name', 'action'])
    const variableNames = this.#extractVariableNames(template);

    // Если переменных нет - этот шаблон не подходит для сопоставления
    if (variableNames.length === 0) {
      return null;
    }

    // Шаг 2: Экранируем специальные символы regex в шаблоне
    // 'User {name}.' -> 'User \\{name\\}\\.'
    let pattern = this.#escapeRegex(template);

    // Шаг 3: Заменяем экранированные переменные на захватывающие группы
    // 'User \\{name\\}\\.' -> 'User (.+?)\\.'
    pattern = pattern.replace(ESCAPED_VARIABLE_PATTERN, CAPTURING_GROUP_PATTERN);

    // Шаг 4: Создаем регулярное выражение для сопоставления
    const regex = new RegExp(`^${pattern}$`);
    const match = message.match(regex);

    // Если сообщение не соответствует паттерну - возвращаем null
    if (!match) {
      return null;
    }

    // Шаг 5: Формируем объект параметров из захваченных значений
    // match[0] - полное совпадение, match[1], match[2]... - захваченные группы
    const params: Record<string, string> = {};

    variableNames.forEach((name, index) => {
      params[name] = match[index + 1];
    });

    return params;
  }

  /**
   * Извлекает имена переменных из шаблона
   *
   * @param template - шаблон с переменными
   * @returns массив имен переменных
   *
   * @example
   * extractVariableNames('User {name} tried {action}')
   * // => ['name', 'action']
   */
  #extractVariableNames(template: string): string[] {
    const matches = template.matchAll(TEMPLATE_VARIABLE_PATTERN);

    return Array.from(matches, (m) => m[1]);
  }

  /**
   * Экранирует специальные символы regex в строке
   *
   * @param str - строка для экранирования
   * @returns строка с экранированными специальными символами
   *
   * @example
   * escapeRegex('test.name')
   * // => 'test\\.name'
   */
  #escapeRegex(str: string): string {
    return str.replace(REGEX_SPECIAL_CHARS, ESCAPE_REPLACEMENT);
  }
}
